import { Observable } from 'rxjs';
import { Address2ComponentValue } from './component/address2-component.domain';
import { EntityTypes } from './component/entity-type-component.domain';
import type { SavePartyChangedField } from './application-service.domain';
import {
  KybEntityResult,
  KybManualPerson,
  KybPerson,
  KybPersonRowState,
  KybReconciliationSummary,
  KybResultStatus,
  KybStakeholderRoleFlags,
  KybValidationResult,
  kybIsAdditionalGuarantor,
  kybIsSelectedByDefault,
  kybSortKeyPeople,
  kybValidateCompanyKeyPeople,
} from './kyb-key-people.domain';

/**
 * Shared FE/BE contract for the KYC/AML tab "Business Entities" KYB summary.
 *
 * The displayed KYB status is FrankieOne's workflow result from the curated result row.
 */

export type KybStatusSource =
  | 'workflowResult'
  | 'none';

export interface KybStatusDisplay {
  /** Humanised FrankieOne workflow result, or the no-result label. */
  value: string;
  source: KybStatusSource;
}

export interface KybEntityRegistryDetails {
  registeredName: string | null;
  abn: string | null;
  acn: string | null;
  organizationStatusCode: string | null;
  organizationStatusDescription: string | null;
  organizationTypeCode: string | null;
  organizationTypeDescription: string | null;
  organizationClassCode: string | null;
  organizationClassDescription: string | null;
  organizationSubClassCode: string | null;
  organizationSubClassDescription: string | null;
  registry: string | null;
  registeredCountry: string | null;
  jurisdictionCountry: string | null;
  registeredDate: string | null;
  gstRegisteredFrom: string | null;
  registeredOfficeAddress: string | null;
  principalPlaceOfBusinessAddress: string | null;
}

export interface KybEntityAmlSummary {
  result: string | null;
  state: string | null;
  totalHits: number | null;
  totalTruePositives: number | null;
  totalFalsePositives: number | null;
  totalUnknown: number | null;
  totalUnresolved: number | null;
  unresolvedPep: number | null;
  unresolvedSanction: number | null;
  unresolvedWatchlist: number | null;
  unresolvedAdverseMedia: number | null;
}

export interface KybEntityStakeholderSummary {
  stakeholderId: number;
  /** Null until a save has materialised the stakeholder into an ApplicationIndividual. */
  individualId: number | null;
  frankieOneEntityId: string | null;
  givenName: string;
  middleName: string | null;
  surname: string;
  dateOfBirth: string | null;
  roles: KybStakeholderRoleFlags;
  ownershipPercent: number | null;
  totalShares: number | null;
  beneficialOwnershipPct: number | null;
  nonBeneficialOwnershipPct: number | null;
  directorAppointmentDate: string | null;
  directorStatus: string | null;
  isGuarantor: boolean;
  isSigner: boolean;
  /** Tri-state as on KybPerson: null means never saved, so the entity-type default applies. */
  isSelected: boolean | null;
}

/**
 * A natural person attached to the entity. Trustees and partners are carried because they are the
 * entity's own parties, and signers because the entity row lists who signs for it; the same person can
 * be both, so the roles are flags rather than one kind.
 */
export interface KybEntityIndividualPartySummary {
  individualId: number;
  givenName: string;
  middleName: string | null;
  surname: string;
  dateOfBirth: string | null;
  isTrustee: boolean;
  isPartner: boolean;
  isSigner: boolean;
}

export function kybEntityIndividualPartyName(party: KybEntityIndividualPartySummary): string {
  return [party.givenName, party.middleName, party.surname]
    .map(part => (part ?? '').trim())
    .filter(part => part !== '')
    .join(' ');
}

export function kybEntityIndividualPartyRoleLabel(party: KybEntityIndividualPartySummary): string {
  const roles: string[] = [];
  if (party.isTrustee) {
    roles.push('Trustee');
  }
  if (party.isPartner) {
    roles.push('Partner');
  }
  if (party.isSigner) {
    roles.push('Signer');
  }
  return roles.join(' / ');
}

/** Trustees and partners only: the entity's own natural-person parties, to which KYB does not apply. */
export function kybEntityNaturalPersonParties(
  parties: KybEntityIndividualPartySummary[],
): KybEntityIndividualPartySummary[] {
  return parties.filter(party => party.isTrustee || party.isPartner);
}

export interface KybEntityIssueSummary {
  severity: string | null;
  category: string | null;
  issue: string | null;
  description: string | null;
}

/**
 * The subset of consent-form prefill fields staff may correct from the KYB tab. Only carried for the
 * primary borrowing entity, because buildConsentFormPrefillData resolves them from that entity alone.
 * businessContactEmail is deliberately application-level (Application.Contacts.Email) rather than an
 * ApplicationCommercialEntity column - the consent form prints the application's primary contact.
 */
export interface KybEntityKeyCompanyDetails {
  annualRevenue: number | null;
  areaCode: string | null;
  phoneNumber: string | null;
  businessContactEmail: string | null;
  principalPlaceOfBusinessAddress: Address2ComponentValue;
}

/**
 * One dirty entity to persist in a consolidated "save parties" request. Only the consent-printed
 * company block is editable from the KYB tab, so the payload is exactly KybEntityKeyCompanyDetails -
 * with one exception: businessContactEmail is omitted when the reviewer did not edit it. The API
 * reads an absent email as "untouched" (it neither validates it nor rewrites Application.Contacts),
 * so a stored value that predates email validation cannot fail an unrelated edit to the block.
 */
export interface SaveApplicationPartyEntityInput {
  commercialEntityId: number;
  data: Omit<KybEntityKeyCompanyDetails, 'businessContactEmail'> & { businessContactEmail?: string | null };
  /**
   * The company-block fields the reviewer edited, with old/new values for the entity's audit rows.
   * `data` always carries the whole block, so the server cannot infer the edited fields from it.
   */
  changedFields?: SavePartyChangedField[];
}

// Human labels for the KybCompanyDetailsValue change keys, used by the entity audit row in the
// save-parties flow.
export const KYB_COMPANY_DETAILS_FIELD_LABELS: { [key: string]: string } = {
  annualRevenue: 'Annual revenue',
  businessPhone: 'Business phone',
  businessContactEmail: 'Business contact email',
  principalPlaceOfBusinessAddress: 'Principal place of business',
};

export const kybCompanyDetailsFieldLabel = (key: string): string => KYB_COMPANY_DETAILS_FIELD_LABELS[key] ?? key;

/** A single named check (blocklist / AML screening) rendered as a card in the expanded row. */
export interface KybEntityCheckCard {
  title: string;
  /** FrankieOne's own token where the payload carries one; never synthesised. */
  resultToken: string | null;
  /** Plain-English reading of the counts / issues FrankieOne returned. */
  outcome: string;
  /** null when the check did not run for this entity. */
  passed: boolean | null;
}

export interface KybEntitySummary {
  commercialEntityId: number;
  entityName: string;
  entityType: EntityTypes;
  isPrimary: boolean;
  isBorrower: boolean;
  isGuarantor: boolean;
  /** The broker selected the sticky manual KYB workflow for this commercial entity. */
  isManualKybMode: boolean;
  parentCommercialEntityId: number | null;
  isPartnerOfParent: boolean;
  isTrusteeOfParent: boolean;
  /** True when at least one curated ApplicationKYBResult row exists for this entity. */
  hasKybResult: boolean;
  /** True when a newer run exists than the result currently displayed. */
  showingPreviousResult: boolean;
  /** FrankieOne organization id associated with the displayed result. */
  frankieOneEntityId: string | null;
  /** True when FrankieOne's REPORT step stored a documentId on the displayed result. */
  kybReportAvailable: boolean;
  riskScore: number | null;
  riskLevel: string | null;
  workflowStatus: string | null;
  workflowResult: string | null;
  workflowExecutionState: string | null;
  /** Portal ApplicationKYBRun.status; null when the entity has never had a KYB run. */
  runStatus: KybResultStatus | null;
  /**
   * ApplicationKYBRun.id behind the displayed result, which a risk re-evaluation re-scores. May be
   * older than the latest run while a newer one is pending. Null when there is no result.
   */
  resultKybRunId: number | null;
  /** UTC instant after which another billable workflow may be started for this entity. */
  workflowCanRunAt: string | null;
  registry: KybEntityRegistryDetails;
  aml: KybEntityAmlSummary | null;
  issueCount: number;
  issues: KybEntityIssueSummary[];
  stakeholders: KybEntityStakeholderSummary[];
  /** Broker-entered people for entities that used the manual KYB fallback. */
  manualPeople: KybManualPerson[];
  /** Internal workflow only; this contract is returned by the staff KYB summary endpoint. */
  reconciliation: KybReconciliationSummary | null;
  /** Individual trustees/partners of this entity (natural persons; KYB does not apply). */
  individualParties: KybEntityIndividualPartySummary[];
  /** Populated for the primary borrowing entity only; null for every other entity. */
  keyCompanyDetails: KybEntityKeyCompanyDetails | null;
  /** Corporate trustees/partners nested under this entity. */
  children: KybEntitySummary[];
}

export interface KybEntitySummaryResult {
  applicationId: number;
  entities: KybEntitySummary[];
}

export type KybEntitySummaryFn = (applicationId: number) => Observable<KybEntitySummaryResult>;

/** Title-case an UPPER_SNAKE FrankieOne result for display (e.g. MANUAL_REVIEW -> Manual Review). */
const humaniseStatus = (status: string): string =>
  status
    .split('_')
    .filter(part => part.length > 0)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

export function kybEntityStatusDisplay(entity: Pick<
  KybEntitySummary,
  'workflowResult'
>): KybStatusDisplay {
  const workflowResult = entity.workflowResult?.trim();
  if (workflowResult) {
    return { value: humaniseStatus(workflowResult), source: 'workflowResult' };
  }
  return { value: 'No KYB result', source: 'none' };
}

/** Tooltip provenance for the status chip. */
export function kybStatusSourceLabel(source: KybStatusSource): string {
  switch (source) {
    case 'workflowResult':
      return 'FrankieOne workflow result';
    case 'none':
      return 'No KYB result for this entity';
  }
}

export function kybEntityRoleLabel(entity: Pick<
  KybEntitySummary,
  'isTrusteeOfParent' | 'isPartnerOfParent' | 'isGuarantor'
>): string {
  if (entity.isTrusteeOfParent) {
    return 'Trustee';
  }
  if (entity.isPartnerOfParent) {
    return 'Partner';
  }
  if (entity.isGuarantor) {
    return 'Additional Guarantor';
  }
  return 'Borrowing Entity';
}

export function kybEntityTypeLabel(entityType: EntityTypes): string {
  switch (entityType) {
    case 'sole-trader':
      return 'Sole Trader';
    case 'company':
      return 'Company';
    case 'trust':
      return 'Trust';
    case 'partnership':
      return 'Partnership';
    default:
      return 'Other';
  }
}

/**
 * Title-cased risk rating, matching how the individuals table above renders FrankieOne's risk level
 * ("Low" / "Medium" / "High"). Null when FrankieOne returned no level, so the caller can show the
 * not-available dash rather than an empty cell.
 */
export function kybEntityRiskLevelLabel(level: string | null): string | null {
  const token = (level ?? '').trim();
  if (!token) {
    return null;
  }
  return `${token.charAt(0).toUpperCase()}${token.slice(1).toLowerCase()}`;
}

/**
 * CSS-class token used to colour the rating by severity. Taken from FrankieOne's riskLevel and never
 * re-derived from the score, so the colour always agrees with the rating shown.
 */
export function kybEntityRiskLevelClass(level: string | null): string {
  return (level ?? '').trim().toLowerCase();
}

/** Numeric FrankieOne risk score, or null when none was returned. 0 is a real score. */
export function parseKybRiskScore(score: unknown): number | null {
  if (score == null || score === '') {
    return null;
  }
  const value = Number(score);
  return Number.isFinite(value) ? value : null;
}

/** Risk score as displayed; null when FrankieOne returned none. */
export function kybEntityRiskScoreLabel(score: number | null): string | null {
  const value = parseKybRiskScore(score);
  if (value == null) {
    return null;
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** One row of the registration details panel in the expanded entity row. */
export interface KybEntityRegistryRow {
  label: string;
  /** Null renders as the not-available dash. */
  value: string | null;
  kind: 'text' | 'date' | 'status';
  /** Status rows only: true/false colour the pill; null leaves it neutral. */
  positive: boolean | null;
}

// A registry reports a live registration as Active (ABR) or Registered (ASIC); anything else means
// the registration has lapsed and should read as a negative.
const LIVE_REGISTRATION_TOKENS = ['active', 'registered'];

const textRow = (label: string, value: string | null | undefined): KybEntityRegistryRow => ({
  label,
  value: (value ?? '').toString().trim() || null,
  kind: 'text',
  positive: null,
});

const dateRow = (label: string, value: string | null | undefined): KybEntityRegistryRow => ({
  label,
  value: (value ?? '').toString().trim() || null,
  kind: 'date',
  positive: null,
});

const statusRow = (label: string, value: string | null | undefined): KybEntityRegistryRow => {
  const token = (value ?? '').toString().trim();
  return {
    label,
    value: token || null,
    kind: 'status',
    positive: token ? LIVE_REGISTRATION_TOKENS.includes(token.toLowerCase()) : null,
  };
};

/**
 * The registration details panel, in the order the design lists them.
 *
 * Every row is a stored field of the KYB result (ApplicationKYBResult and its addresses), falling back
 * to the commercial entity's own record where the registry returned nothing. Nothing here is derived
 * from the application's ABR payload: those facts resolved for one entity only - the one owning the
 * searched ABN - so they read as missing data on every guarantor and trustee row. Rows are always
 * present even when their field is empty, so a reviewer sees a dash meaning "we hold nothing for this"
 * rather than the row silently disappearing.
 */
export function kybEntityRegistryRows(
  entity: Pick<KybEntitySummary, 'entityName' | 'registry'>,
): KybEntityRegistryRow[] {
  const { registry } = entity;
  return [
    textRow('Business Name', registry.registeredName || entity.entityName),
    textRow('Business Type', registry.organizationTypeDescription ?? registry.organizationTypeCode),
    textRow('ABN', registry.abn),
    textRow('ACN', registry.acn),
    statusRow('ACN Status', registry.organizationStatusDescription ?? registry.organizationStatusCode),
    dateRow('ACN Registration Date', registry.registeredDate),
    dateRow('GST Registration Date', registry.gstRegisteredFrom),
    textRow('Registry', registry.registry),
    textRow('Registered Office', registry.registeredOfficeAddress),
    textRow('Principal Place of Business (per registry)', registry.principalPlaceOfBusinessAddress),
  ];
}

/**
 * Officeholder, UBO and shareholder registers come from ASIC, so only an ACN-registered entity has
 * them. A trust is screened on its ABN alone (it has no ACN of its own), so it legitimately shows no
 * registers while its corporate trustee does - that is correct, not missing data.
 */
export function kybEntityHasAcn(entity: Pick<KybEntitySummary, 'registry'>): boolean {
  return !!entity.registry.acn?.trim();
}

/**
 * View the summary's entity as the key-people read model, so the KYC/AML tab can render the same
 * editable Beneficial Owners & Officeholders table the KYB wizard uses.
 *
 * Adapting rather than fetching /get-kyb-result alongside keeps one request and one source of truth:
 * two reads of the same stakeholders can disagree, and the tab would then persist ticks against a
 * table the reviewer was not looking at. kybResultId and addresses are not read by that table, so
 * they are left null rather than carried through the summary contract for no reader.
 */
export function kybEntityAsKeyPeopleResult(entity: KybEntitySummary): KybEntityResult {
  return {
    kybResultId: null,
    commercialEntityId: entity.commercialEntityId,
    entityName: entity.entityName,
    entityType: entity.entityType,
    organizationTypeCode: entity.registry.organizationTypeCode,
    abn: entity.registry.abn,
    acn: entity.registry.acn,
    isPrimary: entity.isPrimary,
    isBorrower: entity.isBorrower,
    isGuarantor: entity.isGuarantor,
    parentCommercialEntityId: entity.parentCommercialEntityId,
    isPartnerOfParent: entity.isPartnerOfParent,
    isTrusteeOfParent: entity.isTrusteeOfParent,
    // Not carried by the summary contract: only the Additional Guarantors screen needs this to
    // guard previous-business removal, and this adapter never feeds that screen.
    source: null,
    // The rules the table enforces only fire on a COMPLETED run, and an incomplete run has no
    // stakeholders to tick anyway, so falling back to the run's own pending status is safe.
    status: entity.runStatus ?? 'IN_PROGRESS',
    mode: entity.isManualKybMode ? 'MANUAL' : 'AUTOMATIC',
    people: kybSortKeyPeople(entity.stakeholders.map(stakeholderAsKybPerson)),
    manualPeople: entity.manualPeople,
    addresses: null,
  };
}

/**
 * The signer/guarantor rule messages for one entity as the reviewer currently has it ticked, so the
 * KYC/AML tab can show the same rules the save enforces while the ticks are being made instead of
 * only as a rejected save.
 *
 * `changes` are the ticks made since load, keyed by stakeholderId. An untouched row is read the way
 * the table renders it, and an unselected person counts as neither role, which is how the save path
 * reads the persisted state.
 *
 * An additional guarantor is exempt from the guarantor rules here as it is on the wizard's guarantors
 * step, so the tab cannot block an edit over ticks the application was never required to make.
 */
export function kybEntityKeyPeopleValidation(
  entity: KybEntitySummary,
  changes: readonly KybPersonRowState[] = [],
): KybValidationResult {
  const result = kybEntityAsKeyPeopleResult(entity);
  // Same gate as the save-time check: the rules only apply once FrankieOne has returned the people.
  if (result.status !== 'COMPLETED' || !result.people.length) {
    return { errors: [], warnings: [] };
  }
  const changeByStakeholder = new Map(changes.map(change => [change.stakeholderId, change]));
  const selections = result.people.map(person => {
    const row = changeByStakeholder.get(person.stakeholderId) ?? renderedRowState(result, person);
    return {
      stakeholderId: person.stakeholderId,
      isSelected: row.isSelected,
      isGuarantor: row.isSelected && row.isGuarantor,
      isSigner: row.isSelected && row.isSigner,
    };
  });
  return kybValidateCompanyKeyPeople(result, entity.entityType, selections, {
    skipGuarantorRules: kybIsAdditionalGuarantor(result),
  });
}

/** An untouched row as the KYC/AML table shows it when lock affordances are off. */
const renderedRowState = (entity: KybEntityResult, person: KybPerson): KybPersonRowState => ({
  stakeholderId: person.stakeholderId,
  isSelected: person.isGuarantor || person.isSigner
    || (person.isSelected ?? kybIsSelectedByDefault(entity.entityType, person.ownershipPercent)),
  isGuarantor: person.isGuarantor,
  isSigner: person.isSigner,
});

const stakeholderAsKybPerson = (stakeholder: KybEntityStakeholderSummary): KybPerson => ({
  stakeholderId: stakeholder.stakeholderId,
  individualId: stakeholder.individualId,
  frankieOneEntityId: stakeholder.frankieOneEntityId,
  givenName: stakeholder.givenName,
  middleName: stakeholder.middleName,
  surname: stakeholder.surname,
  dateOfBirth: stakeholder.dateOfBirth,
  ownershipPercent: stakeholder.ownershipPercent,
  roles: stakeholder.roles,
  isGuarantor: stakeholder.isGuarantor,
  isSigner: stakeholder.isSigner,
  isSelected: stakeholder.isSelected,
});

// FrankieOne reports a blocklist hit as an issue category rather than a dedicated workflow step, so
// the blocklist card is read off the issues array.
const BLOCKLIST_ISSUE_CATEGORY = 'BLOCKLISTED';

export function kybEntityBlocklistCheck(
  entity: Pick<KybEntitySummary, 'hasKybResult' | 'issues'>,
): KybEntityCheckCard {
  if (!entity.hasKybResult) {
    return { title: 'Blocklist', resultToken: null, outcome: 'Not screened', passed: null };
  }
  const hits = entity.issues.filter(i => (i.category ?? '').toUpperCase() === BLOCKLIST_ISSUE_CATEGORY);
  if (!hits.length) {
    return { title: 'Blocklist', resultToken: null, outcome: 'No blocklist match was found', passed: true };
  }
  return {
    title: 'Blocklist',
    resultToken: null,
    outcome: hits.map(h => h.description || h.issue || 'Blocklist match').join('; '),
    passed: false,
  };
}

type KybAmlUnresolvedKey = keyof Pick<
  KybEntityAmlSummary,
  'unresolvedPep' | 'unresolvedSanction' | 'unresolvedWatchlist' | 'unresolvedAdverseMedia'
>;

const AML_UNRESOLVED_CATEGORIES: { key: KybAmlUnresolvedKey; label: string }[] = [
  { key: 'unresolvedPep', label: 'PEP' },
  { key: 'unresolvedSanction', label: 'sanction' },
  { key: 'unresolvedWatchlist', label: 'watchlist' },
  { key: 'unresolvedAdverseMedia', label: 'adverse media' },
];

/**
 * Reads the AML card off the unresolved counts. FrankieOne's own result token is carried through
 * untouched on resultToken; the outcome sentence only restates the counts in words and never
 * overrides that token.
 */
export function kybEntityAmlScreeningCheck(
  entity: Pick<KybEntitySummary, 'aml'>,
): KybEntityCheckCard {
  const aml = entity.aml;
  if (!aml) {
    return { title: 'AML screening', resultToken: null, outcome: 'Not screened', passed: null };
  }
  const hits = AML_UNRESOLVED_CATEGORIES
    .map(category => ({ ...category, count: aml[category.key] ?? 0 }))
    .filter(category => category.count > 0);
  if (!hits.length) {
    return {
      title: 'AML screening',
      resultToken: aml.result,
      outcome: 'No PEP, sanction, watchlist or adverse media match found',
      passed: true,
    };
  }
  return {
    title: 'AML screening',
    resultToken: aml.result,
    outcome: hits.map(h => `${h.count} unresolved ${h.label}`).join(', '),
    passed: false,
  };
}
